import type { ReactNode } from 'react'

/**
 * `scroll` pages own their overflow, so the shell around them — the sidebar on a
 * desktop, the tab bar on a phone — stays put instead of scrolling away with the
 * content. `wide` is for the reading-width pages; Now stays narrow because the
 * grid is a control surface, not a document.
 */
export function Screen({
  children,
  wide = false,
  scroll = false,
}: {
  children: ReactNode
  wide?: boolean
  scroll?: boolean
}) {
  return (
    <main
      className={`mx-auto w-full px-4 pt-3 pb-4 ${wide ? 'max-w-5xl' : 'max-w-lg lg:max-w-3xl'} ${
        // A scrolling page is ordinary block flow. As a flex column it squashed
        // any child that set its own overflow — a flex item with overflow set has
        // an automatic minimum size of zero, so a bordered panel collapsed to a
        // line rather than pushing the content below it down.
        scroll ? 'min-h-0 flex-1 overflow-y-auto overscroll-contain' : 'flex flex-1 flex-col'
      }`}
    >
      {children}
    </main>
  )
}

export function Placeholder({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center">
      <p className="text-sm font-medium text-dim">{title}</p>
      <p className="max-w-xs text-[14px] leading-relaxed text-faint">{body}</p>
    </div>
  )
}
